import React, { useEffect, useMemo, useState } from 'react';
import styles from 'signer-app/signature-modal/signature-modal.module.css';
import classnames from 'classnames';

import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Modal } from '@dropbox/dig-components/modal';
import { Text } from '@dropbox/dig-components/typography';
import { Box, Split } from '@dropbox/dig-foundations';
import constants from 'signer-app/signature-modal/constants';
import { SignatureType } from 'signer-app/signature-modal/types';
import { SignatureModalContext } from 'signer-app/signature-modal/signature-modal-context/context';
import { SigData } from 'signer-app/context/signer-app-client/signature';
import SignatureField, {
  SignatureFieldProps,
} from './signature-field/signature-field';
import SignatureButtons from './signature-buttons';
import useSignatureData from './use-signature-data';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';

const messages = defineMessages({
  addYourSignature: {
    id: '9072d3d5a7916c9a53d10e4e0e97c20ace250fd34fa611253946272b00f378e6',
    description:
      'Title for a modal where a user is able to type or draw in the signature that will appear on the document.',
    defaultMessage: 'Add your signature',
  },
  addYourInitials: {
    id: '76929a3f8298b816f5326ee45e1af910adfe0f005c84c0711362ba5169c73dd1',
    description:
      'Title for a modal where a user is able to type or draw in their initials that will appear on the document.',
    defaultMessage: 'Add your initials',
  },
  footerDisclosureSignature: {
    id: '4a2dd551828c734cb054281c250d4836b06162530e255f37d8d7e60206821e23',
    description:
      'Disclosure text that the user must agree to when signing a document.',
    defaultMessage:
      'I understand this is a legal representation of my signature.',
  },
  footerDisclosureInitials: {
    id: '21560fa03cc1c3cc5e3ed59c632d12a3ea7fd2e3a08d1c5502491a5113606ffb',
    description:
      'Disclosure text that the user must agree to when signing a document.',
    defaultMessage:
      'I understand this is a legal representation of my initials.',
  },
  close: {
    id: 'b56d58676cd724ee4bdfb291f933554fa8fd8739be764004cf93d934744db9df',
    description: 'title text of closing button of modal',
    defaultMessage: 'Close',
  },
  signature: {
    id: '4d1a57e5f937b20260195ec406652cef74a63b6db5f19250fb380e097171edc2',
    description: 'Label for signature field',
    defaultMessage: 'Signature',
  },
  initials: {
    id: '0265b2f972332a7a1150d7c3a94c34b5e801e7eb089722136381bcf22de36970',
    description: 'Label for initials field',
    defaultMessage: 'Initials',
  },
});

interface TypeSpecificMessages {
  fieldLabel: MessageDescriptor;
  contentLabel: MessageDescriptor;
  footerDisclosure: MessageDescriptor;
}

const messagesForSignatureTypes: Record<SignatureType, TypeSpecificMessages> = {
  [constants.TYPE_SIGNATURE]: {
    fieldLabel: messages.signature,
    contentLabel: messages.addYourSignature,
    footerDisclosure: messages.footerDisclosureSignature,
  },
  [constants.TYPE_INITIALS]: {
    fieldLabel: messages.initials,
    contentLabel: messages.addYourInitials,
    footerDisclosure: messages.footerDisclosureInitials,
  },
};

function useIsMobileModalView() {
  const [isMobile, setIsMobile] = useState(false);

  const onResize = () => {
    setIsMobile(window.innerWidth <= 600);
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return isMobile;
}

type SignatureModalBodyProps = {
  showMobileDrawerView?: boolean;
  onInsert: (signatureData: SigData | null, insertEverywhere: boolean) => void;
  createNewSignature: SignatureModalContext['createNewSignature'];
  canInsertEverywhere: boolean;
  getSignatureData: () => SignatureData | null;
  isDisabled: boolean;
  buttonText?: string;
  signatureFieldProps: SignatureFieldProps;
};

function SignatureModalBodyContent({
  showMobileDrawerView,
  buttonText,
  onInsert,
  createNewSignature,
  canInsertEverywhere,
  getSignatureData,
  isDisabled,
  signatureFieldProps,
}: SignatureModalBodyProps) {
  const intl = useIntl();

  const { type, selectedTab } = signatureFieldProps;
  const labels = messagesForSignatureTypes[type];

  return (
    <>
      {showMobileDrawerView && (
        <Split direction="vertical" paddingBottom="Micro Medium">
          <Split.Item>
            <Text variant="label" isBold size="small">
              {intl.formatMessage(labels.fieldLabel)}
            </Text>
          </Split.Item>

          <Split.Item>
            <Text color="subtle">
              {intl.formatMessage(labels.footerDisclosure)}
            </Text>
          </Split.Item>
        </Split>
      )}

      <SignatureField {...signatureFieldProps} />

      {showMobileDrawerView && (
        <Box width="100%" marginY="Micro Large">
          <SignatureButtons
            isDrawerView
            selectedTab={selectedTab}
            canInsertEverywhere={canInsertEverywhere}
            getSignatureData={getSignatureData}
            onInsert={onInsert}
            createNewSignature={createNewSignature}
            disabled={isDisabled}
            buttonText={buttonText}
          />
        </Box>
      )}
    </>
  );
}

function MobileDrawerViewModalContent({
  buttonText,
  onInsert,
  createNewSignature,
  canInsertEverywhere,
  getSignatureData,
  isDisabled,
  signatureFieldProps,
}: SignatureModalBodyProps) {
  const intl = useIntl();

  const { type } = signatureFieldProps;
  const labels = messagesForSignatureTypes[type];

  return (
    <>
      <Box
        borderColor={'Border Subtle'}
        borderWidth={'1'}
        borderBottom={'Solid'}
        padding="Micro Large"
      >
        <Text id="signature-modal-title" variant="label" isBold size="small">
          {intl.formatMessage(labels.contentLabel)}
        </Text>
      </Box>

      <Box padding="Micro Large">
        <SignatureModalBodyContent
          showMobileDrawerView
          buttonText={buttonText}
          onInsert={onInsert}
          createNewSignature={createNewSignature}
          canInsertEverywhere={canInsertEverywhere}
          getSignatureData={getSignatureData}
          isDisabled={isDisabled}
          signatureFieldProps={signatureFieldProps}
        />
      </Box>
    </>
  );
}

export type SignatureModalProps = SignatureModalContext & {
  isOpen: boolean;
  type: SignatureType;
  loading: boolean;
  onClose: () => void;
  onInsert: (signatureData: SigData | null, insertEverywhere: boolean) => void;
  buttonText?: string;
  initialTypeInValue: string;
  isDrawerView?: boolean;
};

export type SignatureData = SigData;

export interface SignatureModalBody {
  getSignatureData: () => SigData | null;
}

type SignatureModalWrapperProps = Omit<
  SignatureModalProps,
  'signatureModalContext'
>;

export default function SignatureModal({
  isOpen,
  onClose,
  selectedTab,
  savedSignatures,
  loading,
  getSignatureUrl,
  onSavedSignatureSelect,
  onSavedSignatureRemove,
  selectedSavedSignature,
  isUploadingSignature,
  uploadedSignature,
  onRotateSignature,
  clearUploadedSignature,
  initialTypeInValue,
  type,
  getPaginatedSignatures,
  initialsCount,
  signaturesCount,
  error,
  buttonText,
  clearSignatureError,
  onInsert,
  createNewSignature,
  canInsertEverywhere,
  onUploadSignature,
  enabledTabs,
  onTabSelect,
  isDrawerView,
}: SignatureModalWrapperProps) {
  const intl = useIntl();
  const labels = messagesForSignatureTypes[type];

  const isMobile = useIsMobileModalView();

  const showMobileDrawerView = useMemo(() => {
    return isMobile && isDrawerView;
  }, [isMobile, isDrawerView]);

  const { signatureModalBodyRef, getSignatureData } = useSignatureData(
    type,
    selectedTab,
  );

  const [enableInsert, setEnableInsert] = useState(false);
  const isDisabled = !enableInsert || loading;

  const onRequestClose = () => {
    clearUploadedSignature();

    if (error && clearSignatureError) {
      clearSignatureError();
    }

    onClose();
  };

  const signatureFieldProps = {
    signatureModalBodyRef,
    type,
    initialTypeInValue,
    loading,
    selectedTab,
    savedSignatures,
    getSignatureUrl,
    onSavedSignatureSelect,
    onSavedSignatureRemove,
    selectedSavedSignature,
    isUploadingSignature,
    uploadedSignature,
    onRotateSignature,
    clearUploadedSignature,
    getPaginatedSignatures,
    initialsCount,
    signaturesCount,
    error,
    clearSignatureError,
    enableInsertButtonCallback: setEnableInsert,
    onUploadSignature,
    enabledTabs,
    onTabSelect,
  };

  return (
    <Modal
      open={isOpen}
      isCentered
      width="large"
      withCloseButton={intl.formatMessage(messages.close)}
      onRequestClose={onRequestClose}
      aria-labelledby="signature-modal-title"
    >
      <WhiteLabelThemeProvider mode="bright">
        {showMobileDrawerView ? (
          <MobileDrawerViewModalContent
            buttonText={buttonText}
            onInsert={onInsert}
            createNewSignature={createNewSignature}
            canInsertEverywhere={canInsertEverywhere}
            getSignatureData={getSignatureData}
            isDisabled={isDisabled}
            signatureFieldProps={signatureFieldProps}
          />
        ) : (
          <>
            <Modal.Header
              className={styles.modalHeader}
              hasBottomSpacing="title-standard"
            >
              <Modal.Title id="signature-modal-title">
                {intl.formatMessage(labels.contentLabel)}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body
              className={classnames([styles.modalBody, styles.modalBodyV2])}
            >
              <SignatureModalBodyContent
                buttonText={buttonText}
                onInsert={onInsert}
                createNewSignature={createNewSignature}
                canInsertEverywhere={canInsertEverywhere}
                getSignatureData={getSignatureData}
                isDisabled={isDisabled}
                signatureFieldProps={signatureFieldProps}
              />
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
              <Text
                id="signature-modal-footer-disclosure"
                size="small"
                className={styles.disclosure}
              >
                {intl.formatMessage(labels.footerDisclosure)}
              </Text>

              <SignatureButtons
                selectedTab={selectedTab}
                canInsertEverywhere={canInsertEverywhere}
                getSignatureData={getSignatureData}
                onInsert={onInsert}
                createNewSignature={createNewSignature}
                disabled={isDisabled}
                buttonText={buttonText}
              />
            </Modal.Footer>
          </>
        )}
      </WhiteLabelThemeProvider>
    </Modal>
  );
}
